import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { tzNormalizeDate } from '../../../../utils/date';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../../../config/locale';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import { numberFormatter } from '../../../../utils/number';

const normalize = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
};

const formatDay = (value) =>
  value ? tzNormalizeDate({ date: value, format: DATE_FORMAT }) : null;

const formatCostCenter = (costCenter) => {
  if (!costCenter) return null;
  return costCenter.costCenterCode
    ? `${costCenter.costCenterCode} - ${costCenter.name || costCenter.description}`
    : costCenter.name || costCenter.description;
};

const formatVoucherType = (voucherType) => {
  if (!voucherType) return null;
  return voucherType.voucherCode
    ? `${voucherType.voucherCode} - ${voucherType.name}`
    : voucherType.name;
};

const getChanges = (migration, expense, transaction) => {
  const fields = [
    {
      label: 'Socio de Negocio',
      original:
        transaction.salesSession && transaction.salesSession.agency
          ? transaction.salesSession.agency.name
          : undefined,
      current: migration.agency ? migration.agency.name : null,
    },
    {
      label: 'Centro de Costo',
      original: formatCostCenter(expense.costCenter),
      current: formatCostCenter(migration.costCenter),
      compareBy: [
        expense.costCenter ? expense.costCenter.id : null,
        migration.costCenter ? migration.costCenter.id : null,
      ],
    },
    {
      label: 'Tipo de Egreso',
      original: transaction.expenseType ? transaction.expenseType.name : null,
      current: migration.expenseTypeName,
    },
    {
      label: 'Fecha de Emisión',
      original: formatDay(transaction.createDate),
      current: formatDay(migration.issueDate),
    },
    {
      label: 'Tipo de Comprobante',
      original: formatVoucherType(transaction.voucherType),
      current: formatVoucherType(migration.voucherType),
      skip: !transaction.voucherTypeId,
    },
    {
      label: 'Serie',
      original: expense.documentSeries,
      current: migration.documentSeries,
    },
    {
      label: 'Correlativo',
      original: expense.documentCode,
      current: migration.documentCode,
    },
    {
      label: 'RUC Proveedor',
      original: transaction.business ? transaction.business.businessTaxId : null,
      current: migration.businessTaxId,
    },
    {
      label: 'Nombre Proveedor',
      original: transaction.business ? transaction.business.name : null,
      current: migration.businessName,
    },
    {
      label: 'Descripción',
      original: expense.description,
      current: migration.description,
    },
    {
      label: 'Importe Total',
      original: numberFormatter({ value: transaction.amount }),
      current: numberFormatter({ value: migration.totalAmount }),
    },
  ];

  return fields.filter((field) => {
    if (field.skip || field.original === undefined) return false;

    if (field.compareBy) {
      return normalize(field.compareBy[0]) !== normalize(field.compareBy[1]);
    }

    return normalize(field.original) !== normalize(field.current);
  });
};

const ExpenseSapMigrationInformationResource = ({ sapMigrations, expense }) => {
  const transactionList = expense.transactionList || [];

  const migrationsWithChanges = sapMigrations
    .map((migration) => {
      const transaction =
        transactionList.find((t) => t.id === migration.transactionId) || {};
      return {
        migration,
        changes: getChanges(migration, expense, transaction),
      };
    })
    .filter(({ changes }) => changes.length > 0);

  if (migrationsWithChanges.length === 0) return null;

  return (
    <>
      <h3>Egreso editado</h3>
      {migrationsWithChanges.map(({ migration, changes }, index) => (
        <Fragment key={migration.id}>
          {migrationsWithChanges.length > 1 && (
            <ResourceProperty label="Transacción: ">
              {migration.transactionId}
            </ResourceProperty>
          )}
          <ResourceProperty label="Editado por: ">
            {migration.editedByUserName || '-'}
          </ResourceProperty>
          {migration.editedDate && (
            <ResourceProperty label="Fecha de edición: ">
              {tzNormalizeDate({
                date: migration.editedDate,
                format: DATE_TIME_FORMAT,
              })}
            </ResourceProperty>
          )}
          {changes.map((change) => (
            <ResourceProperty key={change.label} label={`${change.label}: `}>
              {`${normalize(change.original) || 'Sin valor'} → ${
                normalize(change.display || change.current) || 'Sin valor'
              }`}
            </ResourceProperty>
          ))}
          {index < migrationsWithChanges.length - 1 && <hr />}
        </Fragment>
      ))}
    </>
  );
};

ExpenseSapMigrationInformationResource.propTypes = {
  sapMigrations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  expense: PropTypes.shape({
    transactionList: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default ExpenseSapMigrationInformationResource;